import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { convertDateTimeToUTC, isEmpty } from '../../../common/common';
import { saveScheduleDateTime, saveRawScheduleDateTime } from '../../../store/action/OrderAction';
import { useCallCenterFlag, useCartData, useScheduleDate } from '../../../hooks/order';
import { buildSlotsByDate, parseRawScheduleDateTime, formatLocalDate } from './scheduleUtils';
import ScheduleCalendar from './ScheduleCalendar';
import { DropdownArrowIcon } from '../../../images/svg';

// Native-style date/time selectors for the mobile appointment step - same scheduleAppt data
// and save actions as ScheduleInstallation.js (the desktop calendar), just a different UI.
const MobileDateTimePicker = ({ scheduleAppt, isMobileAdded, setActiveKey }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isAfterHours } = useCallCenterFlag();
  const { rawScheduleDateTime } = useScheduleDate();
  const { spanishLanguagePref } = useCartData();

  const slotsByDate = useMemo(() => buildSlotsByDate(scheduleAppt), [scheduleAppt]);
  const dates = Object.keys(slotsByDate);

  const { date: savedDate, time: savedTime } = useMemo(
    () => parseRawScheduleDateTime(rawScheduleDateTime),
    [rawScheduleDateTime]
  );

  const initialDate = (savedDate && dates.includes(savedDate)) ? savedDate : (dates[0] || '');
  const [selectedDate, setSelectedDate] = useState(initialDate);

  const timeOptions = useMemo(() => slotsByDate[selectedDate] || [], [slotsByDate, selectedDate]);
  const initialTime = (savedTime && timeOptions.includes(savedTime)) ? savedTime : (timeOptions[0] || '');
  const [selectedTime, setSelectedTime] = useState(initialTime);

  const [openPopover, setOpenPopover] = useState(null); // 'date' | 'time' | null

  // Sync selection if scheduleAppt loads asynchronously after initial mount
  React.useEffect(() => {
    if ((!selectedDate || !dates.includes(selectedDate)) && dates.length > 0) {
      const nextDate = (savedDate && dates.includes(savedDate)) ? savedDate : dates[0];
      setSelectedDate(nextDate);
      const nextSlots = slotsByDate[nextDate] || [];
      const nextTime = (savedTime && nextSlots.includes(savedTime)) ? savedTime : (nextSlots[0] || '');
      setSelectedTime(nextTime);
    }
  }, [dates, slotsByDate, savedDate, savedTime, selectedDate]);

  const togglePopover = (name) => setOpenPopover((prev) => (prev === name ? null : name));

  // The Date/Time fields are styled divs, so give them what a native control has: focusable,
  // opened with Enter/Space, and announced as a button that expands a popup.
  const getTriggerProps = (name) => ({
    role: 'button',
    tabIndex: 0,
    'aria-haspopup': 'dialog',
    'aria-expanded': openPopover === name,
    onClick: () => togglePopover(name),
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        togglePopover(name);
      }
    },
  });

  const handleSelectCalendarDate = (dateStr) => {
    setSelectedDate(dateStr);
    const availableSlots = slotsByDate[dateStr] || [];
    setSelectedTime(availableSlots[0] || '');
    setOpenPopover(null);
  };

  const handleSelectTimeSlot = (slot) => {
    setSelectedTime(slot);
    setOpenPopover(null);
  };

  const handleConfirm = () => {
    if (isEmpty(selectedDate) || isEmpty(selectedTime)) {return;}
    const combineDate = `${moment(selectedDate).format('YYYY-MM-DD')} to ${selectedTime}`;
    dispatch(saveScheduleDateTime(convertDateTimeToUTC(combineDate)));
    dispatch(saveRawScheduleDateTime(combineDate));
    setActiveKey(isMobileAdded ? '2' : '3');
  };

  const handleSkip = () => setActiveKey(isMobileAdded ? '2' : '3');

  return (
    <div className="spc-mobile-date-time-picker">
      <div className="spc-mobile-date-time-picker__field">
        <label>{t('singlePageCheckout.dateLabel')}</label>
        <div
          className={`spc-mobile-trigger-field ${openPopover === 'date' ? 'is-open' : ''}`}
          {...getTriggerProps('date')}
        >
          <span>{selectedDate ? formatLocalDate(selectedDate, 'ddd, MMM D', spanishLanguagePref) : t('singlePageCheckout.chooseADate')}</span>
          <span className="spc-trigger-arrow">
            <DropdownArrowIcon />
          </span>
        </div>

        {openPopover === 'date' && (
          <ScheduleCalendar
            dates={dates}
            slotsByDate={slotsByDate}
            selectedDate={selectedDate}
            onSelect={handleSelectCalendarDate}
            spanishLanguagePref={spanishLanguagePref}
          />
        )}
      </div>

      <div className="spc-mobile-date-time-picker__field">
        <label>{t('singlePageCheckout.timeLabel')}</label>
        <div
          className={`spc-mobile-trigger-field ${openPopover === 'time' ? 'is-open' : ''}`}
          {...getTriggerProps('time')}
        >
          <span>
            {selectedTime ? selectedTime.replace(/\s+to\s+/gi, ' – ') : t('singlePageCheckout.chooseATime')}
          </span>
          <span className="spc-trigger-arrow">
            <DropdownArrowIcon />
          </span>
        </div>

        {openPopover === 'time' && (
          <div className="spc-mobile-popover spc-mobile-time-popover">
            <div className="spc-mobile-popover__label">{t('singlePageCheckout.selectTimeLabel')}</div>
            <div className="spc-time-options-list">
              {timeOptions.map((slot) => {
                const formattedSlot = slot.replace(/\s+to\s+/gi, ' – ');
                const isSelected = slot === selectedTime;
                return (
                  <button
                    type="button"
                    key={slot}
                    className={`spc-time-option-btn ${isSelected ? 'is-selected' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectTimeSlot(slot);
                    }}
                  >
                    <span>{formattedSlot}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="spc-mobile-date-time-picker__actions">
        <button
          type="button"
          className="blue-button spc-mobile-save-button"
          onClick={handleConfirm}
          disabled={isEmpty(selectedDate) || isEmpty(selectedTime)}
        >
          {t('singlePageCheckout.saveDateTime')}
        </button>
        {!isAfterHours && (
          <button type="button" className="spc-link-button" onClick={handleSkip}>{t('common.skip')}</button>
        )}
      </div>
    </div>
  );
};

export default MobileDateTimePicker;
